import { useTranslation } from "react-i18next";

import type { IAvailableDoctorTimeProps } from "./types";
import useAvailableTimes from "./useAvailableTimes";

import { Button } from "@/components/atoms/button";
import { Chip } from "@/components/atoms/chip";
import { Typography } from "@/components/atoms/typography";
import { InfoBox } from "@/components/atoms/infoBox";

export default function AvailableDoctorTimeGrid(
  props: IAvailableDoctorTimeProps,
) {
  const { onGetVisitClick, showVisitType, visitType, visitTimeTextMode } =
    props;

  const {
    inPersonVisitData,
    inPersonVisitTime,
    noAvailableTime,
    onlineVisitData,
    onlineVisitTime,
    canShowInPersonVisitTime,
    canShowOnlineVisitTime,
  } = useAvailableTimes(showVisitType, visitType);

  const { t } = useTranslation("form");

  if (noAvailableTime) {
    return (
      <div className={"d-flex my-m1"}>
        <InfoBox type={"noData"} translatedText={"noVisitTime"} fullWidth />
      </div>
    );
  }

  const OnlineVisitButton = (
    <>
      {canShowOnlineVisitTime && (
        <Button
          className={"w-100"}
          mode={"main"}
          label={t("getOnlineVisit")}
          disabled={!onlineVisitData}
          onClick={() =>
            onlineVisitData &&
            onGetVisitClick("online_visit", onlineVisitData.formatted_price)
          }
        />
      )}
    </>
  );

  const InPersonVisitButton = (
    <>
      {canShowInPersonVisitTime && (
        <Button
          className={"w-100"}
          mode={"main"}
          label={t("getInPersonVisit")}
          disabled={!inPersonVisitData}
          onClick={() =>
            inPersonVisitData &&
            onGetVisitClick(
              "in_person_visit",
              inPersonVisitData.formatted_price,
            )
          }
        />
      )}
    </>
  );

  return (
    <div className={"d-flex-column gap-2m1"}>
      <div className={"d-flex-column gap-m1"}>
        {canShowOnlineVisitTime && visitTimeTextMode === "chip" && (
          <div className={"flex-item-center gap-m1"}>
            <Chip name={"online_visit"} value={`${t("online_visit")}: `} />
            <Typography fontSize={"sm"} createSpan>
              {onlineVisitTime || "- - -"}
            </Typography>
          </div>
        )}
        {canShowOnlineVisitTime && visitTimeTextMode === "full" && (
          <Typography fontSize={"md-high"}>
            <Typography variant={"regularCaption"} createSpan>{`${t(
              "firstOnlineTime",
            )}: `}</Typography>
            <Typography fontSize={"sm"} createSpan>
              {onlineVisitTime || "- - -"}
            </Typography>
          </Typography>
        )}

        {canShowInPersonVisitTime && visitTimeTextMode === "chip" && (
          <div className={"flex-item-center gap-m1"}>
            <Chip
              name={"in_person_visit"}
              value={`${t("in_person_visit")}: `}
            />
            <Typography fontSize={"sm"} createSpan>
              {inPersonVisitTime || "- - -"}
            </Typography>
          </div>
        )}
        {canShowInPersonVisitTime && visitTimeTextMode === "full" && (
          <Typography fontSize={"md-high"}>
            <Typography variant={"regularCaption"} createSpan>{`${t(
              "firstInPersonTime",
            )}: `}</Typography>
            <Typography fontSize={"sm"} createSpan>
              {inPersonVisitTime || "- - -"}
            </Typography>
          </Typography>
        )}
      </div>
      <div className={"d-flex-column gap-m1"}>
        {OnlineVisitButton}
        {InPersonVisitButton}
      </div>
    </div>
  );
}
